import type { DataCoverage, DriverInfo, RaceEvent, RaceRecording, SessionCapabilities, SessionSummary, TrackPoint } from './race';

export interface FactLedger {
  sessionKey: string;
  session: SessionSummary;
  drivers: readonly DriverInfo[];
  track: readonly TrackPoint[];
  capabilities?: SessionCapabilities;
  coverage?: DataCoverage;
  revision: number;
  events: readonly RaceEvent[];
}

export function factKey(event: RaceEvent) {
  return `${event.type}:${event.id}`;
}

/** Immutable event ledger. Loading or seeking never discards successful facts. */
export function createFactLedger(recording: RaceRecording): FactLedger {
  return { sessionKey: recording.session.key, session: recording.session, drivers: recording.drivers, track: recording.track, capabilities: recording.capabilities, coverage: recording.coverage, revision: 1, events: dedupe(recording.events) };
}

export function appendFacts(ledger: FactLedger, events: readonly RaceEvent[]): FactLedger {
  if (!events.length) return ledger;
  const merged = dedupe([...ledger.events, ...events]);
  if (merged.length === ledger.events.length && merged.every((event, index) => event === ledger.events[index])) return ledger;
  return { ...ledger, revision: ledger.revision + 1, events: merged };
}

export function factsAt(ledger: FactLedger, atMs: number) {
  const target = Number.isFinite(atMs) ? atMs : 0;
  let low = 0, high = ledger.events.length;
  while (low < high) {
    const middle = (low + high) >>> 1;
    if (ledger.events[middle].atMs <= target) low = middle + 1;
    else high = middle;
  }
  return ledger.events.slice(0, low);
}

function dedupe(events: readonly RaceEvent[]) {
  return [...new Map(events.map(event => [factKey(event), event])).values()].sort((a, b) => a.atMs - b.atMs || factKey(a).localeCompare(factKey(b)));
}

/**
 * Append normalized facts without exposing the mutable ledger to callers.
 * The recording remains the compatibility shape used by the current adapters,
 * while all event identity and correction replacement goes through one seam.
 */
export function appendRecordingFacts(recording: RaceRecording, events: readonly RaceEvent[]): RaceRecording {
  if (!events.length) return recording;
  const next = appendFacts(createFactLedger(recording), events);
  return next.events.length === recording.events.length && next.events.every((event, index) => event === recording.events[index])
    ? recording
    : { ...recording, events: [...next.events] };
}
